'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { HexagonBadge } from '@ironhack/design-system2/icons/hexagon-badge';
import { type ReactElement, useContext, useEffect, useState } from 'react';

import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { Link } from '@/app/components/client/link';
import { type DatoCourse } from '@/lib/datocms/types';
import { courseToBackground } from '@/lib/utils';
import type { AvailableCourseTrack } from '@/types';
import { NavColumnHeading } from '../nav-column-heading';
import { NavItemBadge } from '../nav-item-badge';
import { NavItemIcon } from '../nav-item-icon';
import { NavbarContext } from '../navbar-context';
import {
  type CourseMenuItem,
  type SecondLevelContent,
  type SecondLevelItem,
  type ThirdLevelContent,
  isTarget,
} from '../types';
import { EXCEPTION_MEDIA_QUERY } from '../nav-menu';
import { ThirdLevelMenu } from '../third-level-menu';
import { usePageContext } from '@/hooks';
import { sendEvent } from '@/lib/gtm';

type Props = {
  menu: SecondLevelContent;
  isSelected: boolean;
  title: string;
};

const isCourseItem = (
  content: CourseMenuItem | unknown
): content is CourseMenuItem => Boolean((content as CourseMenuItem).course);

export const getSecondLevelItemText = (item: SecondLevelItem): string =>
  isCourseItem(item) ? item.course.name : item.label;

const getTrackBackground = (
  course: Pick<DatoCourse, 'code'>
): `url(#${string})` =>
  `url(#${courseToBackground[course.code as AvailableCourseTrack]})`;

const SecondLevelItemWrapper = (flexProps: FlexProps): ReactElement => (
  <Flex
    align="center"
    borderRadius={[null, null, null, 'lg']}
    gap={1}
    justify="space-between"
    px={[null, null, null, 1]}
    py={1}
    {...flexProps}
  />
);

export const SecondLevelMenu = (props: Props): ReactElement | null => {
  const { menu, title, isSelected } = props;

  const {
    isSecondLevel,
    isThirdLevel,
    setModalLevelTitle,
    setSecondLevel,
    setThirdLevel,
  } = useContext(NavbarContext);

  const [thirdLevelIndex, setThirdLevelIndex] = useState(-1);

  const {
    pageData: { gaPageType },
  } = usePageContext();

  if (isSelected) setModalLevelTitle(title);

  useEffect(() => {
    if (isSecondLevel) setThirdLevelIndex(-1);
  }, [isSecondLevel, setThirdLevelIndex]);

  const { menuItems, ...headingContent } = menu;

  return (
    <ModuleWrapper
      innerProps={{
        display: 'grid',
        gap: [4, null, null, 3],
        gridTemplateColumns: ['1fr', null, null, '1fr 1fr 1fr'],
        px: [0, null, null, 10],
        sx: {
          [EXCEPTION_MEDIA_QUERY]: { px: 5 },
        },
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: ['0', null, null, '64'],
        spacingTop: ['0', null, null, '24'],
      }}
      outerProps={{
        bg: [null, null, null, 'brand.secondary'],
        display: [
          isSecondLevel || isThirdLevel ? 'block' : 'none',
          null,
          null,
          isSelected ? 'block' : 'none',
        ],
        left: [null, null, null, 0],
        pos: [null, null, null, 'absolute'],
        right: [null, null, null, 0],
        // Desktop menu (absolute) needs to be moved down the navbar (relative) height
        top: [null, null, null, '42.2px'],
        // Large screens needs z-index to avoid images and blog header clipping
        zIndex: [null, null, null, 2],
      }}
    >
      <Flex
        direction="column"
        display={isSecondLevel ? 'flex' : ['none', null, null, 'flex']}
        gap={[4, null, null, 2]}
        w="full"
      >
        <NavColumnHeading {...headingContent} />

        <Box as="ul" listStyleType="none">
          {menuItems.map(
            (item: SecondLevelItem, index: number): ReactElement => {
              const isThirdLevelItemSelected = index === thirdLevelIndex;

              const labelContent = (
                <>
                  {isCourseItem(item) && (
                    <HexagonBadge
                      color={getTrackBackground(item.course)}
                      fill={getTrackBackground(item.course)}
                      size="29px" // Magic #: Gets 24px height (icon has padding)
                    />
                  )}
                  {getSecondLevelItemText(item)}
                  {'badge' in item && item.badge && (
                    <NavItemBadge badge={item.badge} />
                  )}
                </>
              );
              return (
                <Box
                  as="li"
                  data-level="2"
                  key={`second-level-item-${item.id}`}
                >
                  {isTarget(item.content) ? (
                    <SecondLevelItemWrapper>
                      <Flex
                        align="center"
                        as={Link}
                        gap={1}
                        textStyle="m"
                        withoutDecoration
                        {...item.content.target}
                      >
                        {labelContent}
                      </Flex>
                      <NavItemIcon itemType="link" />
                    </SecondLevelItemWrapper>
                  ) : (
                    <SecondLevelItemWrapper
                      onClick={() => {
                        sendEvent({
                          eventAction: 'nav menu_level 2',
                          eventCategory: 'Nav',
                          eventLabel: item.content.eventLabel,
                          pageType: gaPageType,
                        });
                        if (isThirdLevelItemSelected) {
                          setThirdLevelIndex(-1);
                          setSecondLevel();
                        } else {
                          setThirdLevelIndex(index);
                          setThirdLevel();
                        }
                      }}
                      role="button"
                      {...(isThirdLevelItemSelected && {
                        bg: [null, null, null, 'rgb(255 255 255 / 0.3)'],
                        textStyle: [null, null, null, 'mBold'],
                        transition: [null, null, null, 'background 0.25s'],
                      })}
                    >
                      <Flex align="center" gap={1} textStyle="m">
                        {labelContent}
                      </Flex>
                      <NavItemIcon itemType="menu" />
                    </SecondLevelItemWrapper>
                  )}
                </Box>
              );
            }
          )}
        </Box>
      </Flex>
      {menuItems.map((item, index) => (
        <ThirdLevelMenu
          isSelected={thirdLevelIndex === index}
          key={`third-level-menu-${index}`}
          menu={item.content as ThirdLevelContent}
          title={getSecondLevelItemText(item)}
        />
      ))}
    </ModuleWrapper>
  );
};
